import axios, { AxiosResponse } from "axios";

import { getToken } from "./token";

/*
* Адрес API — одно место на всю админку. Пустая строка означает тот же origin, что у самого
* фронта: так работают и прод, и стенды. Для локальной разработки раскомментируйте нужную строку.
* */
export const API_HOST = "";
// export const API_HOST = "http://localhost:5004";
// export const API_HOST = "https://dev-sudo.restify.one";

export const API_URL_REST_ENDPOINT = `${API_HOST}/api/rest/sudo/v1`;

const REST_REQUEST_FAILED_MESSAGE = "Запрос не выполнен";

type RestExecutionStatus = "Finished" | "Failed";

export interface RestApiResponse<T> {
    executionStatus: RestExecutionStatus;
    data: T | null;
    message?: string;
}

const buildRestUrl = (path: string) => `${API_URL_REST_ENDPOINT}${path}`;

const buildRestAuthHeaders = () => ({
    AuthToken: getToken(),
});

const restAxiosConfig = {
    validateStatus: () => true,
} as const;

const getFinishedData = <T>(
    response: RestApiResponse<T> | undefined
): T | null | undefined => {
    if (response?.executionStatus === "Finished") {
        return response.data ?? null;
    }

    return undefined;
};

/*
* Чтение: при любом неуспехе отдаём null, вызывающий показывает состояние «не загрузилось».
* Мутации, наоборот, бросают Error с текстом от бэка — он на русском и годится для тоста.
* */
export const restGet = async <ResponseType>(
    path: string,
    params?: Record<string, unknown>
): Promise<ResponseType | null> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> = await axios.get(
        buildRestUrl(path),
        {
            ...restAxiosConfig,
            headers: buildRestAuthHeaders(),
            params,
        }
    );

    const data = getFinishedData(result.data);

    return data === undefined ? null : data;
};

const mutate = async <ResponseType>(
    method: "post" | "put" | "delete",
    path: string,
    body?: unknown
): Promise<ResponseType> => {
    const result: AxiosResponse<RestApiResponse<ResponseType>> = await axios.request({
        ...restAxiosConfig,
        method,
        url: buildRestUrl(path),
        headers: buildRestAuthHeaders(),
        data: body,
    });

    const data = getFinishedData(result.data);

    if (data === undefined) {
        throw new Error(result.data?.message || REST_REQUEST_FAILED_MESSAGE);
    }

    return data as ResponseType;
};

export const restPost = <ResponseType>(path: string, body?: unknown) =>
    mutate<ResponseType>("post", path, body);

export const restPut = <ResponseType>(path: string, body?: unknown) =>
    mutate<ResponseType>("put", path, body);

export const restDelete = <ResponseType>(path: string, body?: unknown) =>
    mutate<ResponseType>("delete", path, body);
